/**
 * Opening the operating system's file picker, in a way iOS does not lose.
 *
 * The obvious implementation builds an `<input type="file">`, calls `click()`
 * on it and never puts it in the document. That works everywhere except the
 * place it matters most: on iOS Safari the element is unreachable the instant
 * the calling function returns, and it can be collected while the photo picker
 * is still open. Choosing ten photographs takes a while — longer when iCloud
 * has to fetch them first — and if the input goes away in that window the
 * `change` event never fires.
 *
 * Nothing throws. Nothing is logged. The picker closes, no upload begins, and
 * the photographs simply appear not to have been chosen. It cannot be seen from
 * our side at all, because no code of ours ever runs.
 *
 * So the element goes into the document and stays there until it has been
 * answered. It is positioned off-screen rather than `display: none`, which some
 * versions of Safari decline to open.
 */

interface PickOptions {
  accept: string;
  multiple?: boolean;
  onPick: (files: File[]) => void;
}

export function openFilePicker({
  accept,
  multiple = false,
  onPick,
}: PickOptions): void {
  const input = document.createElement("input");
  input.type = "file";
  input.accept = accept;
  if (multiple) input.multiple = true;
  input.style.cssText =
    "position:fixed;inset-block-start:0;inset-inline-start:0;inline-size:1px;block-size:1px;opacity:0;pointer-events:none";

  let answered = false;

  const remove = () => {
    // Deferred: Safari dispatches `change` before the `File` handles are safe
    // to detach the element from, and removing it synchronously has been seen
    // to invalidate the very handles just handed over.
    setTimeout(() => input.remove(), 0);
  };

  input.addEventListener("change", () => {
    answered = true;
    const files = input.files ? [...input.files] : [];
    if (files.length > 0) onPick(files);
    remove();
  });

  /*
   * Cancelling fires `cancel` where it is supported and nothing at all where it
   * is not, so the fallback is the next time the window regains focus. Without
   * one of the two, every abandoned pick would leave an input in the document
   * for the life of the page.
   */
  input.addEventListener("cancel", () => {
    answered = true;
    remove();
  });

  window.addEventListener(
    "focus",
    () => {
      // Generous, and only if nothing else answered: returning from the picker
      // restores focus *before* `change` arrives, and tidying up in that gap
      // would recreate the very bug this file exists to prevent.
      setTimeout(() => {
        if (!answered) input.remove();
      }, 2000);
    },
    { once: true },
  );

  document.body.append(input);
  input.click();
}
